import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getErrorMessage } from 'utils/error';
import { programsAPI } from 'api';
import { LeftMenuJobSeekerJoinedProgramsState, LeftMenuJobSeekerJoinedProgramsTableView } from './types';
import { JobSeekerProgramMembership } from 'features/job-seeker-explore-programs/types';
import { Paging, TableTabFilters } from 'api/types';
import { RootState } from 'app/root-reducer';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import initialRequestData from 'features/initial-request-data';

export const getLeftMenuJobSeekerJoinedPrograms = createAsyncThunk<TableDataResponse<LeftMenuJobSeekerJoinedProgramsTableView>, void, RejectWithMessage>(
  'left-menu-job-seeker-joined-programs/get-programs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.leftMenuJobSeekerJoinedPrograms.requestData;
      return await programsAPI.getJobSeekerTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: LeftMenuJobSeekerJoinedProgramsState = {
  status: 'idle',
  error: null,
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
    filters: { tab: JobSeekerProgramMembership.MyGroups },
  },
  data: {
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
};

const leftMenuJobSeekerJoinedPrograms = createSlice({
  name: 'leftMenuJobSeekerJoinedPrograms',
  initialState,
  reducers: {
    setLeftMenuJobSeekerJoinedProgramsPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setLeftMenuJobSeekerJoinedProgramsFilters: (state, action: PayloadAction<Partial<TableTabFilters>>) => {
      state.requestData.filters = { ...state.requestData.filters, ...action.payload };
      state.requestData.paging = initialRequestData.paging;
    },
    resetLeftMenuJobSeekerJoinedProgramsRequestData: (state) => {
      state.requestData = initialState.requestData;
    },
    resetLeftMenuJobSeekerJoinedProgramsPaging: (state) => {
      state.requestData.paging = initialState.requestData.paging;
    },
    resetLeftMenuJobSeekerJoinedProgramsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getLeftMenuJobSeekerJoinedPrograms.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getLeftMenuJobSeekerJoinedPrograms.fulfilled, (state, action: PayloadAction<TableDataResponse<LeftMenuJobSeekerJoinedProgramsTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getLeftMenuJobSeekerJoinedPrograms.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  }
});

export const { setLeftMenuJobSeekerJoinedProgramsFilters, setLeftMenuJobSeekerJoinedProgramsPage, resetLeftMenuJobSeekerJoinedProgramsRequestData, resetLeftMenuJobSeekerJoinedProgramsPaging, resetLeftMenuJobSeekerJoinedProgramsState } = leftMenuJobSeekerJoinedPrograms.actions;
export const leftMenuJobSeekerJoinedProgramsReducer = leftMenuJobSeekerJoinedPrograms.reducer;
